/**
 * Tipos primitivos y enumeraciones del dominio.
 *
 * Estos son los "ladrillos" usados por el resto de tipos. Si necesitas un
 * tipo nuevo, añádelo aquí y úsalo desde donde haga falta.
 */

// ─── Roles ───────────────────────────────────────────────────────────────

/**
 * Rol de un piloto. Permisos acumulativos: cada rol incluye los de los
 * inferiores. Ver ARQUITECTURA.md sección 5.
 */
export type Role = 'superadmin' | 'admin' | 'officer' | 'pilot';

/** Helper: ¿el rol A tiene al menos los permisos de B? */
export const ROLE_RANK: Record<Role, number> = {
  pilot: 1,
  officer: 2,
  admin: 3,
  superadmin: 4,
};

export function hasRole(role: Role, minimum: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

// ─── Estados ─────────────────────────────────────────────────────────────

export type PilotStatus = 'active' | 'inactive';
export type CampaignStatus = 'active' | 'finished';
export type MissionStatus = 'planned' | 'flown' | 'cancelled';
export type MissionType = 'combat' | 'training' | 'test';
export type Visibility = 'private' | 'public';

/**
 * Imán que cuelga el papel de una campaña/misión en la pizarra
 * `/operaciones` (Fase 8). El imán determina visualmente la inclinación
 * del papel — el ángulo concreto vive en CSS, aquí solo el enum.
 *  - `alas`     → inclinado izquierda
 *  - `beer`     → recto
 *  - `estrella` → inclinado derecha
 */
export type MagnetType = 'alas' | 'beer' | 'estrella';

// ─── Eventos de misión ───────────────────────────────────────────────────

/**
 * Tipos de eventos detectables en un .acmi parseado.
 *
 * Los eventos como "kill" no aparecen explícitamente en el .acmi como
 * `Event=Kill`. Se infieren correlacionando aparición de armas y
 * desaparición de objetos. Ver Fase 5d para detalles del parser.
 */
export type MissionEventType =
  | 'takeoff'
  | 'landing'
  | 'kill'
  | 'crash'
  | 'eject'
  | 'hit'
  | 'shot'
  | 'damage'
  | 'mission_end';

// ─── NOTAMs ──────────────────────────────────────────────────────────────

export type NotamCategory = 'info' | 'weather' | 'operational' | 'admin';
export type Priority = 'low' | 'normal' | 'urgent';

// ─── Medallas ────────────────────────────────────────────────────────────

/** Cómo se otorga una medalla. Permite reglas automáticas o manuales. */
export type AwardCriteria =
  | { kind: 'manual' }
  | { kind: 'auto_missions_in_campaign'; campaignId: string; minMissions: number }
  | { kind: 'auto_kills'; minKills: number; killType?: 'air' | 'ground' | 'any' }
  | { kind: 'auto_hours'; minHours: number };

// ─── Identificadores ─────────────────────────────────────────────────────

/** ID genérico de una entidad (UUID v4 en string). */
export type Id = string;

/** Callsign del piloto, p.ej. "51_Blade". Único por escuadrón. */
export type Callsign = string;

/** ID legible de un reporte oficial. Formato MR-YYYY-NNN, p.ej. "MR-2026-001". */
export type MissionReportNumber = string;

// ─── Fechas e intervalos ─────────────────────────────────────────────────

/** Timestamp ISO 8601 en UTC, p.ej. "2026-05-06T14:30:00.000Z". */
export type IsoTimestamp = string;

/** Posición geográfica en el mundo de DCS (lat/lon en grados, alt en metros). */
export interface GeoPosition {
  lat: number;
  lon: number;
  alt: number;
}

// ─── Resultado HTTP genérico ─────────────────────────────────────────────

export interface ApiError {
  error: string;
  code?: string;
  details?: unknown;
}
